import { useState, useCallback, useRef, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { reportError } from '@/lib/error-reporting';

type AssessmentMethod = 'ai_conversation' | 'values_card_sort' | 'reflection_prompts' | 'quick_assessment';
type EventType = 'method_selected' | 'started' | 'step_completed' | 'value_selected' | 'value_deselected' | 'completed' | 'abandoned' | 'ai_conversation_turn' | 'card_sort_category' | 'reflection_response' | 'quick_assessment_score';

interface AssessmentSession {
  id: string;
  method: AssessmentMethod;
  startedAt: Date;
  stepReached: number;
  totalSteps: number;
  conversationTurns?: number;
}

export const useValueAssessmentTelemetry = () => {
  const { user } = useAuth();
  const [currentSession, setCurrentSession] = useState<AssessmentSession | null>(null);
  const sessionStartTime = useRef<Date | null>(null);
  const abandonedRef = useRef<boolean>(false);

  // Track page unload (abandonment detection)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentSession && !abandonedRef.current) {
        // Use sendBeacon for reliable tracking on page unload
        const abandonData = {
          assessment_id: currentSession.id,
          step_reached: currentSession.stepReached,
          time_spent_seconds: sessionStartTime.current 
            ? Math.round((Date.now() - sessionStartTime.current.getTime()) / 1000)
            : 0,
        };
        
        // Try to use sendBeacon for reliability during unload
        if (navigator.sendBeacon) {
          const blob = new Blob([JSON.stringify(abandonData)], { type: 'application/json' });
          navigator.sendBeacon(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-assessment-abandon`,
            blob
          );
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [currentSession]);

  const getDeviceType = (): string => {
    if (typeof window === 'undefined') return 'desktop';
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  };

  const getMethodTotalSteps = (method: AssessmentMethod): number => {
    switch (method) {
      case 'ai_conversation': return 10; // ~10 conversation turns
      case 'values_card_sort': return 5;  // Introduction + 4 sorting rounds
      case 'reflection_prompts': return 6; // 5 prompts + final selection
      case 'quick_assessment': return 15; // 15 statements to rate
      default: return 10;
    }
  };

  const startAssessment = useCallback(async (method: AssessmentMethod, source?: string) => {
    if (!user) return null;

    const totalSteps = getMethodTotalSteps(method);
    sessionStartTime.current = new Date();

    try {
      const { data, error } = await supabase
        .from('value_assessment_analytics')
        .insert({
          user_id: user.id,
          assessment_method: method,
          started_at: sessionStartTime.current.toISOString(),
          total_steps: totalSteps,
          device_type: getDeviceType(),
          source: source || 'dashboard',
        })
        .select('id')
        .single();

      if (error) {
        reportError(error, 'values-assessment', { action: 'start-assessment', method });
        return null;
      }

      const session: AssessmentSession = {
        id: data.id,
        method,
        startedAt: sessionStartTime.current,
        stepReached: 0,
        totalSteps,
      };

      setCurrentSession(session);

      await supabase
        .from('profiles')
        .update({
          values_assessment_method: method,
          values_assessment_started_at: sessionStartTime.current.toISOString(),
        })
        .eq('user_id', user.id);

      // Log start event
      await logEvent('started', { method });

      // Emit engagement event (fire-and-forget)
      import('@/lib/engagement-events').then(({ emitEngagementEvent }) => {
        emitEngagementEvent('values_started', { method });
      }).catch(() => {});

      return session;
    } catch (err) {
      reportError(err, 'values-assessment', { action: 'start-assessment', method });
      return null;
    }
  }, [user]);

  const logEvent = useCallback(async (
    eventType: EventType,
    eventData?: Record<string, unknown>
  ) => {
    if (!user) return;

    try {
      const insertData = {
        user_id: user.id,
        event_type: eventType,
        event_data: eventData || {},
        ...(currentSession?.id ? { assessment_id: currentSession.id } : {}),
      };
      
      await supabase.from('value_assessment_events').insert(insertData);
    } catch (err) {
      reportError(err, 'values-assessment', { action: 'log-event', eventType });
    }
  }, [user, currentSession]);

  const updateProgress = useCallback(async (stepReached: number, additionalData?: Record<string, unknown>) => {
    if (!user || !currentSession) return;

    try {
      const timeSpent = sessionStartTime.current 
        ? Math.round((Date.now() - sessionStartTime.current.getTime()) / 1000)
        : 0;

      await supabase
        .from('value_assessment_analytics')
        .update({
          step_reached: stepReached,
          time_spent_seconds: timeSpent,
          ...additionalData,
        } as any)
        .eq('id', currentSession.id);

      setCurrentSession(prev => prev ? { ...prev, stepReached } : null);

      await logEvent('step_completed', { step: stepReached });
    } catch (err) {
      reportError(err, 'values-assessment', { action: 'update-progress', stepReached });
    }
  }, [user, currentSession, logEvent]);

  const completeAssessment = useCallback(async (
    valuesSelected: string[],
    methodSpecificData?: Record<string, unknown>
  ) => {
    if (!user || !currentSession) return;

    const completedAt = new Date();
    const timeSpent = sessionStartTime.current 
      ? Math.round((completedAt.getTime() - sessionStartTime.current.getTime()) / 1000)
      : 0;

    try {
      // Update analytics record
      await supabase
        .from('value_assessment_analytics')
        .update({
          completed_at: completedAt.toISOString(),
          time_spent_seconds: timeSpent,
          values_selected: valuesSelected,
          values_count: valuesSelected.length,
          step_reached: currentSession.totalSteps,
          ...methodSpecificData,
        } as any)
        .eq('id', currentSession.id);

      // Update profile
      await supabase
        .from('profiles')
        .update({
          values_assessment_completed_at: completedAt.toISOString(),
        })
        .eq('user_id', user.id);

      await logEvent('completed', {
        values_count: valuesSelected.length,
        time_spent_seconds: timeSpent,
      });

      // Emit engagement event (fire-and-forget)
      import('@/lib/engagement-events').then(({ emitEngagementEvent }) => {
        emitEngagementEvent('values_completed', { valuesCount: valuesSelected.length });
      }).catch(() => {});

      setCurrentSession(null);
      sessionStartTime.current = null;
    } catch (err) {
      reportError(err, 'values-assessment', { action: 'complete-assessment' });
    }
  }, [user, currentSession, logEvent]);

  const abandonAssessment = useCallback(async (reason?: string) => {
    if (!user || !currentSession || abandonedRef.current) return;
    
    abandonedRef.current = true;

    const abandonedAt = new Date();
    const timeSpent = sessionStartTime.current 
      ? Math.round((abandonedAt.getTime() - sessionStartTime.current.getTime()) / 1000)
      : 0;

    try {
      await supabase
        .from('value_assessment_analytics')
        .update({
          abandoned_at: abandonedAt.toISOString(),
          time_spent_seconds: timeSpent,
        })
        .eq('id', currentSession.id);

      await logEvent('abandoned', { 
        reason,
        step_reached: currentSession.stepReached,
        time_spent_seconds: timeSpent,
      });

      setCurrentSession(null);
      sessionStartTime.current = null;
    } catch (err) {
      reportError(err, 'values-assessment', { action: 'abandon-assessment' });
    }
  }, [user, currentSession, logEvent]);

  const trackValueSelection = useCallback(async (valueName: string, selected: boolean) => {
    await logEvent(selected ? 'value_selected' : 'value_deselected', { value: valueName });
  }, [logEvent]);

  const trackMethodSelected = useCallback(async (method: AssessmentMethod) => {
    await logEvent('method_selected', { method });
  }, [logEvent]);

  // AI Conversation specific tracking
  const trackAIConversationTurn = useCallback(async () => {
    if (!currentSession) return;
    
    const newTurnCount = (currentSession.conversationTurns || 0) + 1;
    setCurrentSession(prev => prev ? { ...prev, conversationTurns: newTurnCount } : null);
    
    await logEvent('ai_conversation_turn', { turn_number: newTurnCount });
    
    // Update analytics record with turn count
    await supabase
      .from('value_assessment_analytics')
      .update({ ai_conversation_turns: newTurnCount })
      .eq('id', currentSession.id);
  }, [currentSession, logEvent]);

  // Card sort specific tracking
  const trackCardSortCategory = useCallback(async (category: string, values: string[]) => {
    await logEvent('card_sort_category', { category, values, count: values.length });
  }, [logEvent]);

  // Reflection prompts specific tracking
  const trackReflectionResponse = useCallback(async (promptNumber: number, responseLength: number) => {
    await logEvent('reflection_response', { prompt_number: promptNumber, response_length: responseLength });
  }, [logEvent]);

  // Quick assessment specific tracking
  const trackQuickAssessmentScore = useCallback(async (statementNumber: number, score: number) => {
    await logEvent('quick_assessment_score', { statement_number: statementNumber, score });
  }, [logEvent]);

  // Mark completion to prevent abandonment tracking
  const markCompleted = useCallback(() => {
    abandonedRef.current = true;
  }, []);

  /** Restore a session from a saved draft's assessment ID. Falls back to startAssessment if the row is gone. */
  const restoreSession = useCallback(async (assessmentId: string, method: AssessmentMethod) => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('value_assessment_analytics')
        .select('id, assessment_method, started_at, step_reached, total_steps, ai_conversation_turns')
        .eq('id', assessmentId)
        .maybeSingle();

      if (error || !data) {
        // Analytics row was cleaned up — start fresh
        return startAssessment(method, 'draft_resume');
      }

      const session: AssessmentSession = {
        id: data.id,
        method: data.assessment_method || method,
        startedAt: new Date(data.started_at),
        stepReached: data.step_reached || 0,
        totalSteps: data.total_steps || getMethodTotalSteps(method),
        conversationTurns: data.ai_conversation_turns || 0,
      };

      sessionStartTime.current = session.startedAt;
      abandonedRef.current = false;
      setCurrentSession(session);

      await logEvent('started', { method, resumed: true });

      return session;
    } catch (err) {
      reportError(err, 'values-assessment', { action: 'restore-session', assessmentId });
      return startAssessment(method, 'draft_resume');
    }
  }, [user, startAssessment, logEvent]);

  return {
    currentSession,
    startAssessment,
    restoreSession,
    updateProgress,
    completeAssessment,
    abandonAssessment,
    trackValueSelection,
    trackMethodSelected,
    trackAIConversationTurn,
    trackCardSortCategory,
    trackReflectionResponse,
    trackQuickAssessmentScore,
    markCompleted,
    logEvent,
  };
};
